import { RESLINK } from './constants';
import type { RetargetingCandidate } from './retargeting.types';

function parseDate(value: string | undefined): number | null {
  if (!value) return null;
  const ms = Date.parse(value);
  return Number.isNaN(ms) ? null : ms;
}

function expirationOf(candidate: RetargetingCandidate): number | null {
  if (candidate.useCase !== RESLINK) return null;
  return parseDate(candidate.eventExpirationDate);
}

function findEarliestExpiringIndex(candidates: RetargetingCandidate[]): number {
  let bestIndex = -1;
  let bestExpiration = Infinity;
  let bestTimestamp = Infinity;

  candidates.forEach((candidate, index) => {
    const expiration = expirationOf(candidate);
    if (expiration === null) return;

    const timestamp = parseDate(candidate.timestamp) ?? Infinity;
    const expiresEarlier = expiration < bestExpiration;
    const tiedButOlder = expiration === bestExpiration && timestamp < bestTimestamp;

    if (bestIndex === -1 || expiresEarlier || tiedButOlder) {
      bestIndex = index;
      bestExpiration = expiration;
      bestTimestamp = timestamp;
    }
  });

  return bestIndex;
}

function findEarliestTimestampIndex(candidates: RetargetingCandidate[]): number {
  // Uniform loop: treat every candidate the same way, never seed with index 0.
  // If nothing has a parseable timestamp, fall back to index 0 so the caller
  // always gets a valid eviction target.
  let bestIndex = -1;
  let bestTimestamp = Infinity;

  candidates.forEach((candidate, index) => {
    const timestamp = parseDate(candidate.timestamp);
    if (timestamp === null) return;

    if (bestIndex === -1 || timestamp < bestTimestamp) {
      bestIndex = index;
      bestTimestamp = timestamp;
    }
  });

  return bestIndex === -1 ? 0 : bestIndex;
}

export function pickEvictionIndex(candidates: RetargetingCandidate[]): number {
  if (candidates.length === 0) return -1;

  const expiringIndex = findEarliestExpiringIndex(candidates);
  if (expiringIndex !== -1) return expiringIndex;

  return findEarliestTimestampIndex(candidates);
}
